/** Thin router for welcome plan-tour phase cards (slot / duration / invite / outro). */
import { useEffect, useRef } from 'react';
import { ONBOARDING_HINT_NEXT_ACTION_PANEL } from '@/constants/onboardingHints';
import { useAuth } from '@/features/auth/useAuth';
import { markHintSeen } from '@/services/userService';
import OnboardingWelcomePlanTourSlot from '@/features/onboarding/components/OnboardingWelcomePlanTourSlot';
import OnboardingWelcomePlanTourDuration from '@/features/onboarding/components/OnboardingWelcomePlanTourDuration';
import OnboardingWelcomePlanTourInvite from '@/features/onboarding/components/OnboardingWelcomePlanTourInvite';
import OnboardingWelcomePlanTourOutro from '@/features/onboarding/components/OnboardingWelcomePlanTourOutro';

/**
 * @param {import('@/features/onboarding/hooks/useOnboardingWelcomePlanTour').ReturnType} tour
 */
const OnboardingWelcomePlanTour = ({
  active,
  phase,
  outroVariant,
  dumpHint,
  newDumpCount = 0,
  markNextActionPanelSeenInSlot,
  slotDragHintKind,
  skipInvite,
  completeSlot,
  completeDuration,
  closeOutro,
}) => {
  const { user } = useAuth();
  const markedNextActionPanelRef = useRef(false);

  useEffect(() => {
    if (
      !active ||
      phase !== 'slot' ||
      !markNextActionPanelSeenInSlot ||
      !user?.uid ||
      markedNextActionPanelRef.current
    ) {
      return;
    }
    markedNextActionPanelRef.current = true;
    void markHintSeen(user.uid, ONBOARDING_HINT_NEXT_ACTION_PANEL);
  }, [active, phase, markNextActionPanelSeenInSlot, user?.uid]);

  if (!active) {
    return null;
  }

  if (phase === 'slot') {
    return (
      <OnboardingWelcomePlanTourSlot
        slotDragHintKind={slotDragHintKind}
        completeSlot={completeSlot}
      />
    );
  }

  if (phase === 'duration') {
    return (
      <OnboardingWelcomePlanTourDuration completeDuration={completeDuration} />
    );
  }

  if (phase === 'invite') {
    return (
      <OnboardingWelcomePlanTourInvite
        dumpHint={dumpHint}
        newDumpCount={newDumpCount}
        skipInvite={skipInvite}
      />
    );
  }

  if (phase === 'outro') {
    return (
      <OnboardingWelcomePlanTourOutro
        outroVariant={outroVariant}
        closeOutro={closeOutro}
      />
    );
  }

  return null;
};

export default OnboardingWelcomePlanTour;
